import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { countryLabel, flagFromCode } from "@/lib/countries";
import ClientOnboardingWizard from "@/components/ClientOnboardingWizard";
import {
  ArrowRight, CheckCircle2, CheckCircle, Globe2, Users, BarChart3, Sparkles, FileBox,
  Building2, TrendingUp, ShieldCheck, Languages, MapPin, Quote, PhoneCall, ClipboardCheck,
} from "lucide-react";

function _unused() { return null; }

export default function Landing() {
  const { t, lang } = useI18n();
  const [partners, setPartners] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/public/partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/public/partners/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  const T = {
    el: {
      pill: "Ενοικίαση τμήματος πωλήσεων στο εξωτερικό",
      h1Pre: "Πουλήστε ελληνικά προϊόντα",
      h1Mid: "σε όλο τον κόσμο",
      h1Suf: "— χωρίς να ανοίξετε γραφείο.",
      sub: "Νοικιάστε ένα έμπειρο τμήμα πωλήσεων στη χώρα-στόχο σας. Οι συνεργάτες μας μιλούν τη γλώσσα, ξέρουν την αγορά και προωθούν τα προϊόντα ή τις υπηρεσίες σας. Όλα τα αποτελέσματα — κλήσεις, συμφωνίες, έσοδα — παρακολουθούνται ζωντανά μέσα στην πλατφόρμα.",
      ctaPartners: "Ανακαλύψτε συνεργάτες",
      ctaStart: "Νοικιάστε εκπρόσωπο",
      trustHead: "Ένα ολοκληρωμένο τμήμα πωλήσεων στο εξωτερικό, χωρίς το ρίσκο μιας πρόσληψης.",
      howTitle: "Πώς δουλεύει",
      howSub: "Τρία βήματα από την Ελλάδα στις διεθνείς αγορές.",
      step1Title: "Επιλέξτε χώρα & συνεργάτη",
      step1Desc: "Ανακαλύψτε επαγγελματίες πωλήσεων σε κάθε αγορά-στόχο. Δείτε υπηρεσίες, εμπειρία, γλώσσες, κλάδους.",
      step2Title: "Στήστε δημόσιο προφίλ εξαγωγέα",
      step2Desc: "Ανεβάστε προϊόντα, πιστοποιήσεις και κατάλογο. Λάβετε αιτήματα που μετατρέπονται αυτόματα σε leads.",
      step3Title: "Μετρήστε αποτελέσματα live",
      step3Desc: "Παρακολουθήστε κλήσεις, ευκαιρίες, συμφωνίες και έσοδα ανά χώρα και ανά συνεργάτη — σε πραγματικό χρόνο.",
      servicesTitle: "Οι υπηρεσίες μας",
      servicesSub: "Όσα κάνει ένα ολόκληρο τμήμα πωλήσεων — με χρέωση ανά ώρα.",
      services: [
        { t: "Εύρεση προμηθευτών & buyers στο εξωτερικό", d: "Έρευνα αγοράς, ταυτοποίηση πελατών-στόχων και επαφή με decision-makers στη χώρα σας ενδιαφέροντος." },
        { t: "Εμπορική αλληλογραφία & προτάσεις", d: "Εμπορικές προτάσεις, follow-ups και τιμοκατάλογοι στη γλώσσα της κάθε αγοράς." },
        { t: "Cold calls & τηλεφωνικές πωλήσεις", d: "Επαγγελματικές κλήσεις σε buyers, distributors και importers — με αναφορά αποτελεσμάτων σε real time." },
        { t: "Παρουσιάσεις προϊόντων", d: "Online ή face-to-face παρουσιάσεις του brand σας σε υποψήφιους πελάτες, με προσαρμογή στην τοπική αγορά." },
        { t: "Face-to-face συναντήσεις", d: "Ο συνεργάτης σας πηγαίνει αυτοπροσώπως σε ραντεβού, εκθέσεις και επισκέψεις πελατών στην αγορά-στόχο." },
        { t: "Διαπραγμάτευση & κλείσιμο συμφωνιών", d: "Από την πρώτη επαφή μέχρι την υπογραφή — κάθε βήμα της πώλησης καταγράφεται στο CRM σας." },
      ],
      whyTitle: "Γιατί επιχειρήσεις επιλέγουν εμάς",
      benefits: [
        { t: "Τοπικός συνεργάτης πωλήσεων", d: "Κάποιος που γνωρίζει τη γλώσσα, τη νοοτροπία και τις σχέσεις της αγοράς-στόχου." },
        { t: "Καμία πρόσληψη, καμία γραφειοκρατία", d: "Πλήρως ευέλικτο μοντέλο. Δεν χρειάζεται να ανοίξετε γραφείο ή να μισθώσετε προσωπικό στο εξωτερικό." },
        { t: "Διαφάνεια 100%", d: "Κάθε κλήση, συνάντηση και ευκαιρία καταγράφεται στο CRM. Καμία «μαύρη τρύπα»." },
        { t: "Δίγλωσσο GR / EN", d: "Όλη η πλατφόρμα — μενού, αναφορές, ειδοποιήσεις — αλλάζει με ένα κλικ." },
        { t: "Μετρήσιμα KPIs ανά χώρα", d: "Ποια αγορά αποδίδει; Ποιος συνεργάτης φέρνει αποτέλεσμα; Δείτε το με νούμερα." },
        { t: "Δημόσια σελίδα εξαγωγέα", d: "Επαγγελματικό προφίλ ανά εταιρεία με κατάλογο προϊόντων και φόρμα αιτημάτων." },
      ],
      partnersTitle: "Το δίκτυο συνεργατών μας",
      partnersSub: "Έμπειροι επαγγελματίες πωλήσεων σε κρίσιμες αγορές της Ευρώπης και πέρα.",
      seeAll: "Δείτε όλους τους συνεργάτες",
      finalCtaH: "Έτοιμοι να εξάγετε χωρίς ρίσκο;",
      finalCtaSub: "Πληρώνετε μόνο για τις ώρες που χρειάζεστε. Καμία δέσμευση, καμία πρόσληψη — αποτελέσματα από την πρώτη εβδομάδα.",
      seeNetwork: "Δείτε το δίκτυο",
    },
    en: {
      pill: "Rent a sales team abroad",
      h1Pre: "Sell Greek products",
      h1Mid: "around the world",
      h1Suf: "— without opening an office.",
      sub: "Rent an experienced sales team in your target country. Our partners speak the language, know the market and actively promote your products or services. Every result — calls, deals, revenue — is tracked live inside the platform.",
      ctaPartners: "Discover partners",
      ctaStart: "Rent a sales rep",
      trustHead: "A full-stack sales department abroad — without the risk of hiring.",
      howTitle: "How it works",
      howSub: "Three steps from Greece to international markets.",
      step1Title: "Pick a country & partner",
      step1Desc: "Browse sales professionals in every target market. See services, experience, languages, industries.",
      step2Title: "Launch your export profile",
      step2Desc: "Upload products, certifications, and your catalogue. Receive inquiries that auto-convert into CRM leads.",
      step3Title: "Measure results live",
      step3Desc: "Track calls, opportunities, deals and revenue by country and by partner — in real time.",
      servicesTitle: "What we do",
      servicesSub: "Everything a full sales department does — on an hourly basis.",
      services: [
        { t: "Finding suppliers & buyers abroad", d: "Market research, target identification and outreach to decision-makers in your target country." },
        { t: "Commercial correspondence & proposals", d: "Commercial proposals, follow-ups and pricing — in the local language." },
        { t: "Cold calls & phone sales", d: "Professional calls to buyers, distributors and importers — with real-time reporting." },
        { t: "Product presentations", d: "Online or in-person presentations of your brand, adapted to the local market." },
        { t: "Face-to-face meetings", d: "Your partner attends meetings, trade shows and customer visits on the ground." },
        { t: "Negotiation & deal closing", d: "From first contact to signed deal — every step logged in your CRM." },
      ],
      whyTitle: "Why companies choose us",
      benefits: [
        { t: "A local sales partner", d: "Someone who knows the language, culture and relationships of your target market." },
        { t: "No hiring. No paperwork.", d: "Fully flexible. No need to open an office or payroll people abroad." },
        { t: "100% transparency", d: "Every call, meeting and opportunity logged in the CRM. No black box." },
        { t: "Bilingual GR / EN", d: "The entire platform — menus, reports, notifications — switches with one click." },
        { t: "Country-level KPIs", d: "Which market performs? Which partner delivers? See it in numbers." },
        { t: "Public export page", d: "A professional company profile with product catalogue and inquiry form." },
      ],
      partnersTitle: "Our partner network",
      partnersSub: "Experienced sales professionals across critical European markets and beyond.",
      seeAll: "See all partners",
      finalCtaH: "Ready to export without risk?",
      finalCtaSub: "You pay only for the hours you need. No commitment, no hiring — results from the first week.",
      seeNetwork: "See the network",
    },
  }[lang];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <div className="w-9 h-9 rounded-md bg-[#0052CC] flex items-center justify-center text-white font-bold">GE</div>
            <span className="font-heading font-bold text-slate-900">{t("app.name")}</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <a href="#how" className="hover:text-slate-900">{T.howTitle}</a>
            <Link to="/hire-rep" className="hover:text-slate-900" data-testid="landing-hire-rep-link">
              {lang === "el" ? "Πρόσληψη Πωλητή Εξωτερικού" : "Hire Foreign Sales Rep"}
            </Link>
            <Link to="/directories" className="hover:text-slate-900" data-testid="landing-directories-link">
              {lang === "el" ? "Επαγγελματικοί Κατάλογοι" : "Business Directories"}
            </Link>
            <Link to="/contact" className="hover:text-slate-900" data-testid="landing-contact-link">{lang === "el" ? "Επικοινωνία" : "Contact"}</Link>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login" data-testid="landing-login-link" className="px-4 py-2 text-sm font-semibold bg-[#0052CC] text-white rounded-md hover:bg-[#0043A8] transition-colors">
              {t("auth.login")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F1E3D] to-[#0052CC]" />
          <div
            className="absolute inset-0 opacity-25 mix-blend-screen"
            style={{
              backgroundImage:
                "url('https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          {/* glow */}
          <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-emerald-500/20 blur-[120px]" />
          <div className="absolute -bottom-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#0052CC]/40 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-40 text-white">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white text-xs uppercase tracking-[0.25em] font-semibold mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {T.pill}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
              {T.h1Pre}{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-emerald-400 via-cyan-300 to-emerald-400 bg-clip-text text-transparent">
                  {T.h1Mid}
                </span>
              </span>
              {" "}{T.h1Suf}
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-200 max-w-3xl leading-relaxed">
              {T.sub}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="#join"
                data-testid="hero-register-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-slate-900 rounded-md font-semibold hover:bg-slate-100 transition-colors"
              >
                {T.ctaStart}
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/register"
                data-testid="hero-join-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-white/30 text-white rounded-md font-semibold hover:bg-white/10 transition-colors backdrop-blur-sm"
              >
                {lang === "el" ? "Έχω ήδη εταιρεία" : "I already have a company"}
              </Link>
            </div>

            {/* Mini stats */}
            <div className="mt-16 grid grid-cols-3 max-w-2xl gap-4 lg:gap-8 pt-8 border-t border-white/10">
              <Stat n={40} label={lang === "el" ? "Συνεργάτες" : "Partners"} />
              <Stat n={20} label={lang === "el" ? "Χώρες" : "Countries"} />
              <Stat n="100%" label={lang === "el" ? "Διαφάνεια KPI" : "KPI transparency"} />
            </div>
          </div>
        </div>

        {/* Diagonal cut */}
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-white" style={{ clipPath: "polygon(0 100%, 100% 100%, 100% 0)" }} />
      </section>

      {/* Two Services Cards */}
      <section className="bg-white py-20 lg:py-24 border-b border-slate-200" data-testid="services-cards-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0052CC] mb-3">
              {lang === "el" ? "Οι υπηρεσίες μας" : "Our services"}
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              {lang === "el" ? "Δύο τρόποι να επεκταθείτε στο εξωτερικό" : "Two ways to expand abroad"}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Card 1 — Hire Rep */}
            <Link
              to="/hire-rep"
              data-testid="service-card-hire-rep"
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0A1628] to-[#0052CC] text-white p-8 lg:p-10 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-500/20 blur-3xl group-hover:bg-emerald-500/30 transition-colors" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-emerald-300" />
                </div>
                <h3 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">
                  {lang === "el" ? "Πρόσληψη Πωλητή στο Εξωτερικό" : "Hire a Sales Rep Abroad"}
                </h3>
                <p className="mt-4 text-slate-200 leading-relaxed">
                  {lang === "el"
                    ? "Νοικιάστε έμπειρο πωλητή στη χώρα-στόχο σας. Μιλάει τη γλώσσα, γνωρίζει την αγορά, προωθεί ενεργά τα προϊόντα σας. Πληρώνετε μόνο τις ώρες που δουλεύει."
                    : "Rent an experienced sales rep in your target country. Speaks the language, knows the market, actively promotes your products. Pay only for hours worked."}
                </p>
                <ul className="mt-6 space-y-2 text-sm text-slate-100">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" /> {lang === "el" ? "Από 10 ώρες/εβδομάδα έως full-time" : "From 10 hrs/week to full-time"}</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" /> {lang === "el" ? "Ζωντανό tracking κλήσεων & deals" : "Live call & deal tracking"}</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" /> {lang === "el" ? "Softphone με ελληνικό/διεθνή αριθμό" : "Softphone with GR/intl number"}</li>
                </ul>
                <div className="mt-8 inline-flex items-center gap-2 px-5 py-3 bg-white text-slate-900 rounded-md font-semibold group-hover:bg-slate-100">
                  {lang === "el" ? "Ξεκινήστε τώρα" : "Get started"}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            {/* Card 2 — Business Directories */}
            <Link
              to="/directories"
              data-testid="service-card-directories"
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-emerald-600 to-[#065f46] text-white p-8 lg:p-10 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-cyan-400/20 blur-3xl group-hover:bg-cyan-400/30 transition-colors" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center mb-6">
                  <FileBox className="w-7 h-7 text-cyan-200" />
                </div>
                <h3 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">
                  {lang === "el" ? "Επαγγελματικοί Κατάλογοι Επιχειρήσεων" : "Business Directories"}
                </h3>
                <p className="mt-4 text-emerald-50 leading-relaxed">
                  {lang === "el"
                    ? "Στοχευμένες λίστες επιχειρήσεων εξωτερικού με προσωπικά email & τηλέφωνα υπευθύνων. Έτοιμες για outreach και εξαγωγικές καμπάνιες."
                    : "Targeted lists of foreign companies with personal emails & phones of decision-makers. Ready for outreach and export campaigns."}
                </p>
                <ul className="mt-6 space-y-2 text-sm text-emerald-50">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-200 shrink-0" /> {lang === "el" ? "105M+ επιχειρήσεις · 190+ χώρες" : "105M+ companies · 190+ countries"}</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-200 shrink-0" /> {lang === "el" ? "Προσωπικά emails & τηλέφωνα στελεχών" : "Personal emails & phones of executives"}</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-200 shrink-0" /> {lang === "el" ? "Πακέτα από 70€ + ΦΠΑ" : "Packages from €70 + VAT"}</li>
                </ul>
                <div className="mt-8 inline-flex items-center gap-2 px-5 py-3 bg-white text-slate-900 rounded-md font-semibold group-hover:bg-slate-100">
                  {lang === "el" ? "Δείτε τα πακέτα" : "View packages"}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust banner */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 text-center">
          <p className="text-base sm:text-lg text-slate-700 font-medium max-w-4xl mx-auto">
            {T.trustHead}
          </p>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="max-w-2xl mb-16">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0052CC] mb-3">
            {T.howTitle}
          </div>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
            {T.howSub}
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Step n="01" icon={Globe2} title={T.step1Title} desc={T.step1Desc} accent="#0052CC" />
          <Step n="02" icon={Building2} title={T.step2Title} desc={T.step2Desc} accent="#10B981" />
          <Step n="03" icon={BarChart3} title={T.step3Title} desc={T.step3Desc} accent="#F59E0B" />
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-white py-24 lg:py-32 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#10B981] mb-3">
              {lang === "el" ? "Τι περιλαμβάνεται" : "What's included"}
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-[#0F172A] tracking-tight leading-tight">
              {T.servicesTitle}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{T.servicesSub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {T.services.map((s, i) => (
              <div key={i} data-testid={`service-${i}`} className="group bg-white border border-slate-200 rounded-xl p-7 hover:-translate-y-1 hover:shadow-lg hover:border-[#2563EB]/40 transition-all duration-300 relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br from-[#2563EB]/10 to-[#10B981]/10 group-hover:scale-150 transition-transform duration-500" />
                <div className="relative">
                  <div className="font-mono text-xs font-bold text-[#2563EB] mb-3">0{i + 1}</div>
                  <h3 className="font-heading font-bold text-[#0F172A] text-lg leading-tight">{s.t}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 bg-gradient-to-br from-[#0F172A] to-[#2563EB] rounded-2xl p-8 md:p-10 text-white text-center relative overflow-hidden">
            <div className="absolute -top-20 right-0 w-72 h-72 rounded-full bg-[#10B981]/20 blur-3xl" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-[0.2em] font-bold mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
                {lang === "el" ? "Διαφανές μοντέλο χρέωσης" : "Transparent pricing"}
              </div>
              <h3 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">
                {lang === "el" ? "Πληρώνετε μόνο τις ώρες που χρειάζεστε" : "Pay only for the hours you need"}
              </h3>
              <p className="mt-4 text-slate-200 max-w-2xl mx-auto">
                {lang === "el"
                  ? "Καμία πρόσληψη, καμία μηνιαία υποχρέωση. Νοικιάζετε τον συνεργάτη σας ανά ώρα — από 10 ώρες την εβδομάδα μέχρι full-time. Όλες οι ώρες καταγράφονται διαφανώς στην πλατφόρμα."
                  : "No hiring, no monthly commitment. Rent your partner by the hour — from 10 hours/week to full-time. Every hour is transparently logged in the platform."}
              </p>
              <a href="#join" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0F172A] rounded-lg font-bold hover:bg-slate-100" data-testid="services-cta">
                {lang === "el" ? "Δείτε αναλυτικά τι ψάχνετε" : "Tell us what you need"} →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section id="why" className="bg-[#F8FAFC] py-24 lg:py-32 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0052CC] mb-3">
              {lang === "el" ? "Πλεονεκτήματα" : "Advantages"}
            </div>
            <h2 className="font-heading text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              {T.whyTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {T.benefits.map((b, i) => (
              <div key={i} data-testid={`benefit-${i}`} className="bg-white border border-slate-200 rounded-lg p-7 hover:-translate-y-1 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-md bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900 mb-2 text-lg">{b.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client onboarding wizard */}
      <section id="join" className="max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB] mb-3">
            {lang === "el" ? "Βρείτε τον ιδανικό συνεργάτη" : "Find your ideal partner"}
          </div>
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-[#0F172A] tracking-tight leading-tight max-w-3xl mx-auto">
            {lang === "el" ? "Πείτε μας τι ψάχνετε. Θα σας βρούμε τον κατάλληλο εκπρόσωπο." : "Tell us what you need. We'll match you with the right sales rep."}
          </h2>
          <p className="mt-5 text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {lang === "el"
              ? "6 σύντομα βήματα. Η ομάδα μας θα αναλύσει τις ανάγκες σας και θα προτείνει τους κορυφαίους συνεργάτες πωλήσεων στις αγορές-στόχους σας."
              : "6 quick steps. Our team will analyze your needs and recommend top sales partners in your target markets."}
          </p>
        </div>
        <ClientOnboardingWizard />
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F1E3D] to-[#0052CC]" />
        <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-emerald-500/30 blur-[120px]" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center text-white">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold tracking-tight">
            {T.finalCtaH}
          </h2>
          <p className="mt-6 text-lg text-slate-200 max-w-2xl mx-auto">{T.finalCtaSub}</p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <a
              href="#join"
              data-testid="final-cta-register"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-slate-900 rounded-md font-semibold hover:bg-slate-100"
            >
              {T.ctaStart} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#0052CC] text-white flex items-center justify-center text-xs font-bold">GE</div>
            <span>© 2026 {t("app.name")}</span>
          </div>
          <div className="flex flex-wrap gap-6">
            <Link to="/contact" className="hover:text-slate-900">{lang === "el" ? "Επικοινωνία" : "Contact"}</Link>
            <Link to="/login" className="hover:text-slate-900">{t("auth.login")}</Link>
            <Link to="/register" className="hover:text-slate-900">{t("auth.register_company")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-heading text-3xl lg:text-4xl font-bold text-white">{n}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-300 mt-1">{label}</div>
    </div>
  );
}

function Step({ n, icon: Icon, title, desc, accent }) {
  return (
    <div className="relative bg-white border border-slate-200 rounded-lg p-8 hover:-translate-y-1 hover:shadow-sm transition-all">
      <div className="font-heading text-6xl font-bold tracking-tighter" style={{ color: `${accent}25` }}>{n}</div>
      <div className="w-12 h-12 rounded-md flex items-center justify-center mt-4" style={{ backgroundColor: `${accent}15`, color: accent }}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-heading text-xl font-semibold text-slate-900 mt-5">{title}</h3>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}
